import React from "react";
import { AlertTriangle, KeyRound, ShieldCheck, ShieldQuestion } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { orDash } from "../../../pages/enduser/ssh-proxy/sshProxyModel";

const mono = { fontFamily: AN_MONO, fontSize: 12 };

// Two different claims, deliberately not merged.
//
//  · integrity is the CONSOLE's check: the bytes still hash to what was received
//    at ingest. We can assert that.
//  · hmac is the PROXY's claim, computed with a key that never leaves the jump
//    box. The console cannot verify it — only someone holding that key can — so
//    it gets no tick. Showing one would have the console claim a guarantee it
//    cannot make.
export default function IntegrityPanel({ content }) {
  const integrity = content?.integrity || "unknown";
  const altered = integrity === "altered";
  const intact = integrity === "intact";

  const tone = altered
    ? { bg: "var(--an-dangerBg)", bd: "var(--an-dangerBd)", fg: "var(--an-dangerFg)" }
    : intact
    ? { bg: "var(--an-successBg)", bd: "var(--an-successBd)", fg: "var(--an-successFg)" }
    : { bg: "var(--an-warnBg)", bd: "var(--an-warnBd)", fg: "var(--an-warnFg)" };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div
        style={{
          display: "flex", gap: 10, alignItems: "flex-start",
          background: tone.bg, border: `1px solid ${tone.bd}`, color: tone.fg,
          borderRadius: 8, padding: altered ? "14px 16px" : "10px 12px",
          fontFamily: AN_FONT, fontSize: altered ? 13.5 : 12.5, lineHeight: 1.5,
          fontWeight: altered ? 600 : 400,
        }}
      >
        {altered ? <AlertTriangle size={18} style={{ marginTop: 1, flexShrink: 0 }} /> : intact ? <ShieldCheck size={15} style={{ marginTop: 1, flexShrink: 0 }} /> : <ShieldQuestion size={15} style={{ marginTop: 1, flexShrink: 0 }} />}
        <div>
          <div>
            {altered
              ? "Matches what was received: NO — these bytes no longer hash to what arrived at ingest."
              : intact
              ? "Matches what was received — the stored bytes still hash to what arrived at ingest."
              : "Matches what was received: unknown — no ingest hash is on file for this recording."}
          </div>
          {altered && (
            <div style={{ fontWeight: 400, marginTop: 5 }}>
              Treat this recording as evidence of nothing until it has been investigated. This warning cannot be dismissed.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: AN_COLORS.bg, border: `1px solid ${AN_COLORS.line}`, borderRadius: 8, padding: "10px 12px" }}>
        <KeyRound size={15} style={{ marginTop: 2, flexShrink: 0, color: AN_COLORS.muted }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: AN_FONT, fontSize: 12.5, color: AN_COLORS.ink2 }}>
            Verifiable with the gateway key — the gateway signed this digest with a key that never leaves the jump box.
            The console cannot check it; anyone holding that key can.
          </div>
          <div style={{ ...mono, color: AN_COLORS.ink, marginTop: 5, wordBreak: "break-all" }}>
            {orDash(content?.hmac)}
          </div>
        </div>
      </div>
    </div>
  );
}
